import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  filter,
  first,
  forEach,
  fromPairs,
  get,
  includes,
  isEmpty,
  isNull,
  isNumber,
  map,
  mapValues,
  max,
  merge,
  pickBy,
  reject,
  sortBy,
} from 'lodash';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import MenuFridge from '../components/MenuFridge';
import menuContext from '../contexts/menu';
import { DATA_SOURCE, FILTER } from '../constants';
import useQueryString from '../../hooks/useQueryString';
import useShoppingCart from '../contexts/shoppingCart';

export default () => {
  const { pathname, search } = useLocation();
  const shoppingCart = useShoppingCart((state) => state.shoppingCart);
  const history = useHistory();
  const { fridgeId: selectedFridgeId } = useParams() || {};
  const { goToFilter } = useQueryString();
  const [isScrolled, setIsScrolled] = useState(false);
  const { isLoading, menuCache, menuData } = useContext(menuContext);
  const {
    currentInventory,
    currentOnlinePurchaseCampaigns,
    dailyFreshPreOrderBlockedFridgeIds,
    fridges: fridgeDict,
    onlineShopMenu,
    onlineShops,
    products,
    productsAvailableForPreOrder,
    shopCampaigns,
  } = menuCache || {};
  const fridgeInventory = get(currentInventory, selectedFridgeId, null);
  const showDailyFreshPreOrder = !includes(
    dailyFreshPreOrderBlockedFridgeIds,
    selectedFridgeId
  ) || false;

  const menuDict = useMemo(() => {
    const weekString = moment().utcOffset(8).startOf('w').format('YYYYMMDD');
    const today = moment().utcOffset(8).startOf('d').format('YYYYMMDD');

    return get(menuData || {}, [weekString, today, selectedFridgeId], null);
  }, [selectedFridgeId, menuData]);

  useEffect(() => {
    if (isLoading) return;

    if (isEmpty((fridgeDict || {})[selectedFridgeId])) {
      const fridgeId = localStorage.getItem('selectedFridge')
        || sortBy(mapValues((fridgeDict || {}), (fridge, id) => ({
          ...fridge,
          id,
        })), 'name')[0].id;

      history.replace({
        pathname: `/menu/${fridgeId}`,
        search,
      });
    }
  }, [isLoading, fridgeDict, selectedFridgeId, history, search]);

  useEffect(() => {
    if (
      isEmpty(goToFilter)
      || isEmpty(selectedFridgeId)
      || isLoading
      || isScrolled
    ) return;

    setIsScrolled(true);

    const menuFilter = document.getElementById('menu-filter');

    if (menuFilter) {
      setTimeout(() => {
        document.getElementById('root').scrollTo({
          top: menuFilter.getBoundingClientRect().top - 110,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [isLoading, isScrolled, goToFilter, selectedFridgeId]);

  const shopCampaignDescriptions = useMemo(() => map(filter(
    shopCampaigns,
    (campaign) => includes(get(campaign, 'fridgeIds'), selectedFridgeId),
  ), 'description'), [shopCampaigns, selectedFridgeId]);

  const data = useMemo(() => {
    if (isLoading) return [];

    const productDict = {};

    // 處理現貨
    forEach(
      merge(menuDict, fridgeInventory),
      (inventory, productId) => {
        const product = get(products, [productId], null);

        if (isNull(product)) return;

        const { price, provider } = product || {};
        const providerId = btoa(encodeURIComponent(provider));

        productDict[productId] = {
          ...product,
          dataSource: DATA_SOURCE.MENU,
          filterDict: { [FILTER.GOODS_STATUS]: true },
          id: productId,
          inventory: isNumber(inventory) ? inventory : 0,
          providerId,
          sellPrice: price,
        };
      }
    );

    // 處理線上商店
    const onGoingOnlineShop = pickBy(
      onlineShops,
      [['fridgeIdDict', selectedFridgeId], true]
    );

    forEach(onGoingOnlineShop, (onlineShop, onlineShopId) => {
      const { productIds, tags } = onlineShop || {};

      forEach(productIds, (productId) => {
        const onlineShopProduct = get(onlineShopMenu, [`${onlineShopId}${productId}`], null);

        if (isNull(onlineShopProduct)) return;

        const product = get(products, [productId], null);

        if (isNull(product)) return;

        const { price } = product || {};

        productDict[`onlineShop-${onlineShopId}${productId}`] = {
          ...product,
          ...onlineShopProduct,
          dataSource: DATA_SOURCE.ONLINE_SHOP,
          filterDict: fromPairs(map(tags, (tag) => [tag, true])),
          id: productId,
          inventory: 3,
          sellPrice: price,
        };
      });
    });

    // 處理一鍵訂購
    if (showDailyFreshPreOrder) {
      forEach(productsAvailableForPreOrder, (availableProduct) => {
        const { id } = availableProduct;

        const product = get(products, [id], null);

        if (isNull(product)) return;

        const { price, provider } = product || {};
        const providerId = btoa(encodeURIComponent(provider));

        productDict[`dailyFreshPreOrder_${id}`] = {
          ...product,
          ...availableProduct,
          dataSource: DATA_SOURCE.DAILY_FRESH_PRE_ORDER,
          filterDict: { [FILTER.PRE_ORDER]: true },
          id,
          inventory: 3,
          providerId,
          sellPrice: price,
        };
      });
    }

    // 處理折扣
    forEach(reject(productDict, ['dataSource', DATA_SOURCE.MENU]), (product) => {
      const { productNumber, price, id } = product || {};
      const isSaasProduct = (productNumber || '').startsWith('SA');
      const campaigns = filter(currentOnlinePurchaseCampaigns || [], (campaign) => {
        const { applicableFridgeIds, applicableProductIds, isSaasCampaign } = campaign || {};

        if (
          !isSaasCampaign
          && isSaasProduct
        ) return false;

        // 如果有指定冰箱且不是 SaaS Campaign 就進行排除，如果沒有則略過
        if (
          !isEmpty(applicableFridgeIds)
          && !(applicableFridgeIds || []).includes(selectedFridgeId)
          && !isSaasCampaign
        ) return false;

        // 如果有指定商品就進行排除，如果沒有則略過
        if (
          !isEmpty(applicableProductIds)
          && !(applicableProductIds || []).includes(id)
        ) return false;

        return true;
      });

      const campaign = first(campaigns);
      const { parameters } = campaign || {};
      const { Y, Z } = parameters || {};

      // eslint-disable-next-line no-nested-ternary
      const sellPrice = campaign
        ? (Y
          ? max([(price || 0) - Y, 0])
          : parseInt((price || 0) * Z, 10)
        )
        : (price || 0);

      product.sellPrice = sellPrice;
    });

    return map(productDict);
  }, [
    currentOnlinePurchaseCampaigns,
    fridgeInventory,
    isLoading,
    menuDict,
    onlineShopMenu,
    onlineShops,
    products,
    productsAvailableForPreOrder,
    selectedFridgeId,
    showDailyFreshPreOrder,
  ]);

  const fridgeOptions = useMemo(
    () => sortBy(map((fridgeDict || {}), (fridge, id) => {
      const { name } = fridge;
      return {
        name,
        id,
      };
    }), 'name'),
    [fridgeDict]
  );

  const onFridgeSelectChange = (fridgeId) => {
    const pathArray = pathname.split('/');

    pathArray[2] = fridgeId;

    localStorage.setItem('selectedFridge', fridgeId);

    history.push({
      pathname: pathArray.join('/'),
      search,
    });
  };

  return (
    <MenuFridge
      data={data}
      fridgeOptions={fridgeOptions}
      onFridgeSelectChange={onFridgeSelectChange}
      selectedFridgeId={selectedFridgeId}
      shopCampaignDescriptions={shopCampaignDescriptions}
      shoppingCartItemCount={shoppingCart.length}
    />
  );
};
